import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ObjectIdLookup } from 'src/app/models/objectIdLookup.model';
import { RolePermission } from 'src/app/models/rolePermission.model';
import {
  MatrixBulkCell,
  MatrixBulkState,
  MatrixColumnCell,
  MatrixGroup,
  MatrixRow,
} from 'src/app/models/rolePermissionMatrix.model';
import {
  CUSTOM_OBJECTS_GROUP,
  countGrantedObjects,
  getObjectDisplayName,
  groupPermissionObjects,
} from 'src/app/shared/utils/role-permission-grouping.util';
import {
  PERMISSION_COLUMNS,
  PermissionColumn,
  PermissionColumnDef,
  READ_MASK,
  columnDisabledReason,
  hasColumn,
  isColumnDisabled,
  setColumn,
  toggleColumn,
  isViewOnlyObject,
} from 'src/app/shared/utils/role-permission-verb.util';

/** Object-list filter for the matrix toolbar (design-spec §4.3, §7 states). `changed` shows only rows whose mask diverges from the last-saved baseline. */
export type MatrixFilter = 'all' | 'granted' | 'changed';

/**
 * Permission matrix for the Roles tab (DASH-3179; design-spec §4.4, §5.1, §5.2).
 *
 * The detail-pane editor mounted in the shell's `.detail-body`: the backend object catalogue grouped
 * into collapsible taxonomy groups (grouping util, never a frontend catalogue), one object per row,
 * and the four legacy columns per row — Read · Create/Update · Delete · Admin (column util). The
 * checkbox is a component-owned `role="checkbox"`, not `mat-checkbox`, so no global `::ng-deep`
 * restyle is needed (DS-5).
 *
 * Column model (reverses design-spec §3 "Treatment A" per PR #1598): Admin is the legacy select-all
 * (255) and the only control that grants `…Hierarchy` writes; while it is held the other three render
 * checked-and-disabled. Read, Create/Update and Delete are otherwise independent checkboxes — each is
 * toggled on its own, with no "write requires Read" gating (dropped per PR #1598 review); GLOBAL_Pii is
 * view-only. There is no scope-mixed "own" chip — a column is granted only when its full mask is set.
 *
 * The toolbar provides object search that persists across role switches (the DASH-3140 repro), an
 * All/Granted/Changed filter, expand/collapse-all and live counts. The group-header per-column
 * bulk-set writes a column across the group's whole object list through `setColumn` — the explicit
 * replacement for the retired per-object Admin-as-select-all behaviour at group scope.
 */
@Component({
  standalone: false,
  selector: 'app-role-permission-matrix',
  templateUrl: './role-permission-matrix.component.html',
  styleUrls: ['./role-permission-matrix.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RolePermissionMatrixComponent implements OnChanges {
  @Input() objects: ObjectIdLookup[] = [];
  @Input() rolePermissions: RolePermission[] = [];

  /**
   * Ids of objects whose working mask diverges from the last-saved baseline (from the container's
   * save diff, part 6). Drives the per-row amber edge and the `Changed` filter (design-spec §7 state 5).
   */
  @Input() dirtyObjectIds: ReadonlySet<string> = new Set<string>();

  /**
   * Ids of objects the server rewrote on the last save (the read-back divergence, part 8/9; design-spec
   * §7 state 8). Carries the per-row amber edge until the divergence banner is dismissed. Kept separate
   * from {@link dirtyObjectIds} so it never feeds the `Changed` filter — a persisted-but-diverged row is
   * not an unsaved edit.
   */
  @Input() divergentObjectIds: ReadonlySet<string> = new Set<string>();

  /** Emits the whole new mask for one object after a column is toggled; the container owns the write. */
  @Output() permissionChange = new EventEmitter<RolePermission>();

  /** Emits the new masks for every object in a group after a group-header bulk-set; the container owns the write. */
  @Output() bulkVerbChange = new EventEmitter<RolePermission[]>();

  groups: MatrixGroup[] = [];
  readonly columns = PERMISSION_COLUMNS;

  /** Toolbar state — held on the component so it persists as the container swaps the selected role (§4.3). */
  query = '';
  filter: MatrixFilter = 'all';

  /** Live catalogue counts for the toolbar (over the full catalogue, not the filtered rows). */
  totalObjects = 0;
  grantedObjects = 0;

  /** True while the search box holds a non-blank query — drives the "kept while you switch roles" pin. */
  hasQuery = false;
  /** True only when every catalogue group is collapsed — toggles the toolbar's expand/collapse-all label. */
  allCollapsed = false;

  private readonly collapsed = new Set<string>();
  /** Every catalogue category in deterministic order — drives expand/collapse-all independently of the filter. */
  private catalogueCategories: string[] = [];
  /** The full (unfiltered) object list per group, so a bulk-set always covers the whole group. */
  private readonly groupObjects = new Map<string, ObjectIdLookup[]>();

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  ngOnChanges(): void {
    this.build();
  }

  /**
   * Switches the toolbar to the `Changed` filter from an outside command — the save-bar **Review** link
   * (design-spec §5.6). Marks for check because this is an imperative call from the container, not one of
   * the matrix's own template events, so `OnPush` would otherwise not re-render.
   */
  showChanged(): void {
    this.onSetFilter('changed');
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Roving arrow-key focus across the checkbox grid (design-spec §11 — keyboard).
   * Left/Right move between the controls of the current row (the four column cells, or a group
   * header's bulk-set cells); Up/Down move to the same column of the nearest row above/below that
   * also carries controls, so focus steps naturally across group headers and object rows alike.
   * Tab order is untouched — every control stays a real tab stop; this only adds arrow traversal.
   */
  onGridKeydown(event: KeyboardEvent): void {
    const horizontal = event.key === 'ArrowLeft' || event.key === 'ArrowRight';
    const vertical = event.key === 'ArrowUp' || event.key === 'ArrowDown';
    if (!horizontal && !vertical) {
      return;
    }
    const current = event.target as HTMLElement | null;
    if (!current || !current.classList.contains('cbx')) {
      return;
    }
    const rowEl = current.closest('.mx-row') as HTMLElement | null;
    if (!rowEl) {
      return;
    }

    let target: HTMLElement | null = null;
    if (horizontal) {
      const cells = Array.from(rowEl.querySelectorAll<HTMLElement>('.cbx'));
      target = cells[cells.indexOf(current) + (event.key === 'ArrowRight' ? 1 : -1)] ?? null;
    } else {
      const rows = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('.mx-row')).filter(row =>
        row.querySelector('.cbx'),
      );
      const nextRow = rows[rows.indexOf(rowEl) + (event.key === 'ArrowDown' ? 1 : -1)] ?? null;
      if (nextRow) {
        const col = Array.from(rowEl.querySelectorAll<HTMLElement>('.cbx')).indexOf(current);
        const nextCells = Array.from(nextRow.querySelectorAll<HTMLElement>('.cbx'));
        target = nextCells[Math.min(col, nextCells.length - 1)] ?? null;
      }
    }

    if (target) {
      event.preventDefault();
      target.focus();
    }
  }

  onQueryChange(event: Event): void {
    this.query = (event.target as HTMLInputElement).value;
    this.build();
  }

  onSetFilter(filter: MatrixFilter): void {
    this.filter = filter;
    this.build();
  }

  onToggleAll(): void {
    if (this.allCollapsed) {
      this.collapsed.clear();
    } else {
      this.catalogueCategories.forEach(category => this.collapsed.add(category));
    }
    this.build();
  }

  clearAll(): void {
    this.query = '';
    this.filter = 'all';
    this.build();
  }

  onToggleGroup(group: MatrixGroup): void {
    if (this.collapsed.has(group.category)) {
      this.collapsed.delete(group.category);
    } else {
      this.collapsed.add(group.category);
    }
    this.build();
  }

  /**
   * Toggle one column for one object. A disabled cell is inert (Admin holds the other three;
   * GLOBAL_Pii is view-only). The new mask is produced by the legacy-parity {@link toggleColumn} —
   * ticking Admin sets 255, un-ticking sets 0.
   */
  onToggleColumn(row: MatrixRow, cell: MatrixColumnCell): void {
    if (cell.disabled) {
      return;
    }
    const current = this.maskFor(row.objectId);
    this.permissionChange.emit({ objectId: row.objectId, permissions: toggleColumn(current, cell.mask, isViewOnlyObject(row.objectId)) });
  }

  /**
   * Sets one column across every object in the group. `nextChecked` grants the column when the group is
   * not already fully granted, otherwise revokes it — the "select-all" semantics with an explicit label,
   * scoped to one column and one group. Each write goes through {@link setColumn} (Admin resolves to
   * 255/0; other columns set/clear their bits).
   */
  onToggleBulk(group: MatrixGroup, cell: MatrixBulkCell): void {
    const permissionsById = this.permissionsById();
    const changes = (this.groupObjects.get(group.category) ?? []).flatMap<RolePermission>(object => {
      const current = permissionsById.get(object.id) ?? 0;
      const isViewOnly = isViewOnlyObject(object.id);
      // Honour the same per-row invariant when bulk-*granting*: skip objects whose column is
      // disabled (a non-Admin column while Admin is held, or a non-Read column on a view-only object)
      // so the bulk-set can never write a mask the row itself forbids. Revokes are always allowed.
      if (cell.nextChecked && isColumnDisabled(current, cell.mask, isViewOnly)) {
        return [];
      }
      return [{ objectId: object.id, permissions: setColumn(current, cell.mask, cell.nextChecked, isViewOnly) }];
    });
    if (changes.length) {
      this.bulkVerbChange.emit(changes);
    }
  }

  public trackByCategory = (_: number, group: MatrixGroup): string => group.category;
  public trackByObjectId = (_: number, row: MatrixRow): string => row.objectId;
  public trackByColumn = (_: number, cell: MatrixColumnCell): PermissionColumn => cell.column;
  public trackByBulkColumn = (_: number, cell: MatrixBulkCell): PermissionColumn => cell.column;
  public trackByColumnDef = (_: number, column: PermissionColumnDef): PermissionColumn => column.column;

  private build(): void {
    const permissionsById = this.permissionsById();
    const catalogue = groupPermissionObjects(this.objects);

    this.catalogueCategories = catalogue.map(group => group.category);
    this.groupObjects.clear();
    catalogue.forEach(group => this.groupObjects.set(group.category, group.objects));

    this.totalObjects = this.objects.length;
    this.grantedObjects = this.objects.filter(object => (permissionsById.get(object.id) ?? 0) !== 0).length;

    this.hasQuery = this.query.trim().length > 0;
    this.allCollapsed =
      this.catalogueCategories.length > 0 && this.catalogueCategories.every(category => this.collapsed.has(category));

    // Group counts and bulk state are computed over the FULL group; only the rows are filtered, and a
    // group with no visible rows under the current search/filter is dropped entirely (design-spec §7 state 4).
    this.groups = catalogue
      .map<MatrixGroup>(group => ({
        category: group.category,
        displayName: group.displayName,
        grantedCount: countGrantedObjects(group.objects, permissionsById),
        totalCount: group.objects.length,
        collapsed: this.collapsed.has(group.category),
        bulkCells: this.bulkCells(group.objects, permissionsById),
        rows: group.objects
          .filter(object => this.isVisible(object, permissionsById))
          .map<MatrixRow>(object => this.toRow(object, permissionsById, group.category)),
      }))
      .filter(group => group.rows.length > 0);
  }

  /** A row is visible when it matches the search query and the active filter. */
  private isVisible(object: ObjectIdLookup, permissionsById: Map<string, number>): boolean {
    const query = this.query.trim().toLowerCase();
    if (query) {
      const haystack = `${getObjectDisplayName(object)} ${object.description ?? ''} ${object.id}`.toLowerCase();
      if (!haystack.includes(query)) {
        return false;
      }
    }
    if (this.filter === 'granted') {
      return (permissionsById.get(object.id) ?? 0) !== 0;
    }
    if (this.filter === 'changed') {
      return this.dirtyObjectIds.has(object.id);
    }
    return true;
  }

  private bulkCells(objects: ObjectIdLookup[], permissionsById: Map<string, number>): MatrixBulkCell[] {
    return PERMISSION_COLUMNS.map<MatrixBulkCell>(column => {
      const applicable = column.mask === READ_MASK ? objects : objects.filter(object => !isViewOnlyObject(object.id));
      const granted = applicable.filter(object => hasColumn(permissionsById.get(object.id) ?? 0, column.mask)).length;
      const state: MatrixBulkState = granted === 0 ? 'off' : granted === applicable.length ? 'on' : 'mixed';
      return { column: column.column, label: column.label, mask: column.mask, state, nextChecked: state !== 'on' };
    });
  }

  private toRow(object: ObjectIdLookup, permissionsById: Map<string, number>, category: string): MatrixRow {
    const mask = permissionsById.get(object.id) ?? 0;
    const isViewOnly = isViewOnlyObject(object.id);
    return {
      objectId: object.id,
      name: getObjectDisplayName(object),
      description: object.description?.trim() ?? '',
      isCustom: category === CUSTOM_OBJECTS_GROUP,
      isDirty: this.dirtyObjectIds.has(object.id) || this.divergentObjectIds.has(object.id),
      cells: PERMISSION_COLUMNS.map<MatrixColumnCell>(column => ({
        column: column.column,
        label: column.label,
        mask: column.mask,
        checked: hasColumn(mask, column.mask),
        disabled: isColumnDisabled(mask, column.mask, isViewOnly),
        disabledReason: columnDisabledReason(mask, column.mask, isViewOnly) ?? '',
      })),
    };
  }

  private permissionsById(): Map<string, number> {
    return new Map((this.rolePermissions ?? []).map(permission => [
      permission.objectId,
      (permission.permissions ?? 0) & 0xFF, // clamp to the valid 8-bit PermissionFlags range
    ]));
  }

  private maskFor(objectId: string): number {
    return this.permissionsById().get(objectId) ?? 0;
  }
}
